import { formatMeasureValue } from '../lib/visualContract'
import { getMeasureMetadata } from '../lib/analyticalQuery'

/**
 * Bar — one bar per row, height = values[measureKey]. Default sort is
 * descending by value, applied HERE at render time only — never
 * persisted (per approved Phase 5B correction: sorting ≠ Top-N; there is
 * no row-count limiting here, all rows the query returned are shown).
 *
 * onRowClick (Phase 6C, optional): when provided, each bar becomes
 * clickable and calls onRowClick(row) with the SAME row object already
 * in `result.rows` — {key, label, values}. This component knows nothing
 * about drill-down, interactionState.js, executeQuery(), or any Adapter
 * — it only reports which row was clicked. All interpretation of that
 * click (is this a valid drill-down? what's the effective query?) is
 * the caller's responsibility, per the approved Phase 6C architecture.
 * Omitting onRowClick preserves the exact prior behavior — bars remain
 * non-interactive, no cursor/hover changes — so every existing consumer
 * of this component is unaffected.
 */
export default function Bar({ result, measureKey, onRowClick }) {
  const rows = result?.rows || []
  const { label } = getMeasureMetadata(measureKey)

  if (rows.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>No data available</div>
  }

  const sorted = [...rows].sort((a, b) => (b.values[measureKey] ?? 0) - (a.values[measureKey] ?? 0))
  const maxValue = Math.max(...sorted.map(r => Math.abs(r.values[measureKey] ?? 0)), 1)

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 10 }}>{label}</div>
      {sorted.map(row => {
        const value = row.values[measureKey] ?? 0
        const widthPct = (Math.abs(value) / maxValue) * 100
        return (
          <div
            key={row.key}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
              cursor: onRowClick ? 'pointer' : 'default',
            }}
          >
            <div style={{ width: 140, fontSize: 11, color: '#4B5563', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.label}
            </div>
            <div style={{ flex: 1, background: '#F3F4F6', borderRadius: 4, height: 18, position: 'relative' }}>
              <div style={{ width: `${widthPct}%`, background: '#1B3A6B', height: '100%', borderRadius: 4 }} />
            </div>
            <div style={{ width: 90, fontSize: 11, fontFamily: 'monospace', textAlign: 'right', flexShrink: 0 }}>
              {formatMeasureValue(measureKey, value)}
            </div>
          </div>
        )
      })}
    </div>
  )
}
